import { useState, type PointerEvent } from 'react';
import type { SalesPerformance } from '@/types/sales-performance.types';
import { compactMoney, longDate, money, shortDate } from './format';
import { useElementWidth } from './use-element-width';

/**
 * Figma Kim-AI 6532:63056 — revenue over time. Drawn as plain SVG so the grid,
 * dots, latest-value pill and tooltip sit exactly where the design puts them.
 */

const CHART_HEIGHT = 186;
const PLOT_LEFT = 44;
const PLOT_RIGHT = 16;
const PLOT_TOP = 20;
const PLOT_BOTTOM = 150;
const Y_TICKS = 5;
/** The design labels every fifth day and always the last. */
const LABEL_EVERY = 5;
const MIN_LABEL_SPACING = 45;
const X_LABEL_BASELINE = 171;

/** Room above the highest day, so the peak never touches the top gridline. */
const HEADROOM = 1.2;

function niceCeiling(peak: number): number {
  const maximum = peak * HEADROOM;
  const magnitude = 10 ** Math.floor(Math.log10(maximum / (Y_TICKS - 1)));
  const step = Math.ceil(maximum / (Y_TICKS - 1) / magnitude) * magnitude;
  return step * (Y_TICKS - 1);
}

function labelledIndexes(total: number, pixelsPerPoint: number): number[] {
  if (total <= 1) return total ? [0] : [];
  const every = Math.max(LABEL_EVERY, Math.ceil(MIN_LABEL_SPACING / pixelsPerPoint / LABEL_EVERY) * LABEL_EVERY);
  const indexes = Array.from({ length: Math.ceil(total / every) }, (_, step) => step * every).filter((index) => index < total);
  // Keep the last day; drop a regular tick that would crowd it.
  if ((total - 1 - (indexes.at(-1) ?? 0)) * pixelsPerPoint < MIN_LABEL_SPACING) indexes.pop();
  return [...indexes, total - 1];
}

export function PerformanceRevenue({ report }: { report: SalesPerformance }) {
  const [card, cardWidth] = useElementWidth<HTMLDivElement>(1196);
  // The plot is inset 14px from each side of the card.
  const width = Math.max(1, cardWidth - 28);
  const [hovered, setHovered] = useState<number | null>(null);
  const points = report.revenueOverTime.map((point) => ({ date: point.date, revenue: Number(point.revenue) }));
  const ceiling = niceCeiling(Math.max(1, ...points.map((point) => point.revenue)));
  const plotWidth = Math.max(1, width - PLOT_LEFT - PLOT_RIGHT);
  const pixelsPerPoint = points.length > 1 ? plotWidth / (points.length - 1) : plotWidth;
  const x = (index: number): number => points.length > 1 ? PLOT_LEFT + index * pixelsPerPoint : width - PLOT_RIGHT;
  const y = (value: number): number => PLOT_BOTTOM - value / ceiling * (PLOT_BOTTOM - PLOT_TOP);
  const line = points.map((point, index) => `${index ? 'L' : 'M'} ${x(index)} ${y(point.revenue)}`).join(' ');
  const area = points.length ? `${line} V ${PLOT_BOTTOM} H ${x(0)} Z` : '';
  const ticks = Array.from({ length: Y_TICKS }, (_, index) => ceiling / (Y_TICKS - 1) * (Y_TICKS - 1 - index));
  const labelled = labelledIndexes(points.length, pixelsPerPoint);
  const lastIndex = points.length - 1;
  const last = points[lastIndex];
  const active = hovered === null ? null : points[hovered];

  const onPointerMove = (event: PointerEvent<SVGSVGElement>): void => {
    if (!points.length) return;
    const offset = event.clientX - event.currentTarget.getBoundingClientRect().left;
    setHovered(Math.min(lastIndex, Math.max(0, Math.round((offset - PLOT_LEFT) / pixelsPerPoint))));
  };

  return <section aria-label='Revenue over time' className='flex w-full flex-col gap-4'>
    <h2 className='text-xs leading-[15px] font-medium whitespace-nowrap text-gray-800'>Revenue over time</h2>
    <div ref={card} className='relative h-[219px] w-full rounded-3xl border border-gray-500 bg-white'>
      {points.length ? <div className='absolute top-[26px] right-[14px] left-[14px]' style={{ height: CHART_HEIGHT }}>
        <svg width={width} height={CHART_HEIGHT} className='block overflow-visible' role='img' aria-label={`Revenue over time, latest ${money(last.revenue, report.currency)}`} onPointerMove={onPointerMove} onPointerLeave={() => setHovered(null)}>
          {ticks.map((tick) => <g key={tick}>
            <line x1={PLOT_LEFT} x2={width - PLOT_RIGHT} y1={y(tick) - 0.5} y2={y(tick) - 0.5} stroke='var(--color-gray-300)' />
            <text x={32} y={y(tick) + 3} textAnchor='end' fontSize={10} fill='var(--color-gray-800)'>{compactMoney(tick, report.currency)}</text>
          </g>)}
          {labelled.map((index) => <g key={index}>
            <line x1={x(index) + 0.5} x2={x(index) + 0.5} y1={PLOT_TOP} y2={PLOT_BOTTOM} stroke='var(--color-gray-300)' strokeDasharray='3.91 2.93' />
            <text x={x(index)} y={X_LABEL_BASELINE} textAnchor='middle' fontSize={11} fill='var(--color-gray-800)'>{shortDate(points[index].date)}</text>
          </g>)}
          <path d={area} fill='var(--color-indigo-500)' opacity={0.13} />
          <path d={line} fill='none' stroke='var(--color-indigo-500)' strokeWidth={2} strokeLinejoin='round' />
          {points.slice(0, -1).map((point, index) => <circle key={point.date} cx={x(index)} cy={y(point.revenue)} r={1.5} fill='var(--color-white)' stroke='var(--color-indigo-500)' strokeWidth={1} />)}
          <line x1={x(lastIndex) - 0.5} x2={x(lastIndex) - 0.5} y1={12} y2={y(last.revenue)} stroke='var(--color-indigo-500)' />
          <circle cx={x(lastIndex)} cy={y(last.revenue)} r={2.75} fill='var(--color-white)' stroke='var(--color-indigo-500)' strokeWidth={1.5} />
        </svg>
        <div className='absolute top-[-10px] flex h-[22px] min-w-[60px] -translate-x-1/2 items-center justify-center rounded-[11px] bg-indigo-500 px-2 text-[10px] font-semibold whitespace-nowrap text-white' style={{ left: x(lastIndex) }}>
          {money(last.revenue, report.currency)}
        </div>
        {active && hovered !== null && <div role='tooltip' className='pointer-events-none absolute flex -translate-x-1/2 -translate-y-full flex-col gap-1 rounded-xl bg-indigo-500 p-1.5 whitespace-nowrap text-white' style={{ left: x(hovered), top: y(active.revenue) - 6 }}>
          <span className='text-[11px]'>{longDate(active.date)}</span>
          <span className='text-[10px] font-semibold'>{money(active.revenue, report.currency)}</span>
        </div>}
      </div> : <p className='flex h-full items-center justify-center text-sm text-gray-800'>No attributed revenue in this period.</p>}
    </div>
  </section>;
}
